// Per-workspace address form configuration (spec: OP-3818 reference table).
// The modal field set follows the WORKSPACE country, whatever the address
// country; the preview follows the ADDRESS country (formatAddress).
import { detectCountryFormat, formatStreetLine } from "./formatAddress";
const L = "organisation.detail.mainInformation";
const line1 = {
    key: "line1",
    labelKey: `${L}.addressLine1`,
    type: "text",
    required: true,
    requiredMessageKey: `${L}.addressLine1Required`,
};
const line2 = { key: "line2", labelKey: `${L}.addressLine2`, type: "text" };
const line3 = { key: "line3", labelKey: `${L}.addressLine3`, type: "text" };
const postalCode = {
    key: "postalCode",
    labelKey: `${L}.addressPostalCode`,
    type: "text",
};
const city = { key: "city", labelKey: `${L}.addressCity`, type: "text" };
const region = {
    key: "region",
    labelKey: `${L}.addressRegion`,
    type: "region",
};
const country = {
    key: "country",
    labelKey: `${L}.addressCountry`,
    type: "country",
};
// The NL "Rue" input: the street lives in line1 (AddressLine) in every workspace.
const nlStreet = {
    key: "line1",
    labelKey: `${L}.addressStreetName`,
    type: "text",
    required: true,
    requiredMessageKey: `${L}.addressStreetNameRequired`,
};
const houseNumber = {
    key: "houseNumber",
    labelKey: `${L}.addressHouseNumber`,
    type: "numeric",
    required: true,
    requiredMessageKey: `${L}.addressHouseNumberRequired`,
};
const houseNumberSuffix = {
    key: "houseNumberSuffix",
    labelKey: `${L}.addressHouseNumberSuffix`,
    type: "text",
};
// Rows of 1 or 2 descriptors: a 2-field row renders as the 2-column grid the
// Figma modal uses for postal code + city.
const FR_ROWS = [
    [line1],
    [line2],
    [line3],
    [postalCode, city],
    [region],
    [country],
];
const NL_ROWS = [
    [nlStreet],
    [houseNumber, houseNumberSuffix],
    [postalCode, city],
    [country],
];
const CA_ROWS = [
    [line1],
    [line2],
    [postalCode, city],
    [region],
    [country],
];
// INT (workspace outside the three target markets) uses the French field set.
const ADDRESS_FIELD_ROWS = {
    FR: FR_ROWS,
    NL: NL_ROWS,
    CA: CA_ROWS,
    INT: FR_ROWS,
};
// Values must match the useAddressCountryOptions option values. INT has no
// single country, so nothing is preselected there.
const WORKSPACE_DEFAULT_COUNTRY = {
    FR: "France",
    NL: "Pays-Bas",
    CA: "Canada",
    INT: "",
};
// Pre-fill for an address whose country the user has not chosen — never a
// replacement for a chosen one.
export function defaultAddressCountry(workspaceCountry) {
    return WORKSPACE_DEFAULT_COUNTRY[workspaceCountry];
}
export function addressFieldRows(layout) {
    return ADDRESS_FIELD_ROWS[layout];
}
export function addressFields(layout) {
    return ADDRESS_FIELD_ROWS[layout].flat();
}
export function availableAddressFieldKeys(layout) {
    return new Set(addressFields(layout).map((f) => f.key));
}
export function requiredAddressFields(layout) {
    return addressFields(layout).filter((f) => f.required);
}
export function requiredAddressFieldsFilled(values, layout) {
    return requiredAddressFields(layout).every((f) => values[f.key].trim());
}
export function hasAddressField(layout, key) {
    return availableAddressFieldKeys(layout).has(key);
}
// What a save persists. A field the layout cannot render is folded into one it can when both
// describe the same street line (a legacy streetName, a house number); one with no such target
// — a complement or a region the layout omits — keeps its stored value, since blanking it here
// destroyed it on the first save of an address written under another layout (OP-9252). The one
// value still dropped is a streetName whose street line1 already holds (carryStreetToLayout).
export function toAddressLayout(values, layout) {
    const projected = { ...values };
    carryStreetToLayout(projected);
    foldHouseNumberIn(projected, layout);
    return projected;
}
// FR, CA and INT render no house number: it joins line1 in the address country's order, the
// order the address table already shows it in. A line1 that already carries a number of its own
// leaves it no room - folding would write the number twice - so it stays in the record, like
// any other field the layout cannot render.
function foldHouseNumberIn(values, layout) {
    if (hasAddressField(layout, "houseNumber") || !values.houseNumber.trim())
        return;
    // An empty line1 would take the bare number as its whole street line, and that "3" then passes
    // the required-field check. Left apart, the number survives and the empty street still blocks.
    if (!values.line1.trim() || lineCarriesItsNumber(values.line1, values.country))
        return;
    values.line1 = formatStreetLine(values, detectCountryFormat(values.country, values.region));
    values.houseNumber = "";
    values.houseNumberSuffix = "";
}
// The address country's own order, and only that one: read the other way round, the year of
// "rue du 8 Mai 1945" is a house number and a legitimate fold is blocked. A heuristic, not a
// guarantee - a line1 missing the space after its number ("3,Rue X") reads as unnumbered.
function lineCarriesItsNumber(line1, country) {
    const trimmed = line1.trim();
    return streetNumberOrder(country) === "numberFirst" ? NUMBER_FIRST.test(trimmed) : NUMBER_LAST.test(trimmed);
}
// A street typed in the one-line field, the details form never saved: the street
// is all that is known.
export function withStreetLineOnly(values, street, layout) {
    return splitHouseNumberOut({
        ...values,
        line1: street,
        line2: "",
        line3: "",
        streetName: "",
        houseNumber: "",
        houseNumberSuffix: "",
        region: "",
    }, layout);
}
// A layout with a house number field (NL) whose number still sits in line1 gets it
// split out, or its required house number would stay empty. The address country
// picks the end the number is read from: "12 rue du 8 Mai 1945" is French.
export function splitHouseNumberOut(values, layout) {
    if (!hasAddressField(layout, "houseNumber") || values.houseNumber.trim() || !values.line1.trim())
        return values;
    const { street, houseNumber, houseNumberSuffix } = splitStreetLine(values.line1, streetNumberOrder(values.country));
    return { ...values, line1: street, houseNumber, houseNumberSuffix };
}
export function streetNumberOrder(country) {
    return detectCountryFormat(country) === "NL" ? "numberLast" : "numberFirst";
}
const REGION_SET_BY_FORMAT = {
    FR: "FR",
    CA_EN: "CA",
    CA_FR: "CA",
    NL: "NONE",
    generic: "FR",
};
const REGION_SET_BY_WORKSPACE = {
    FR: "FR",
    INT: "FR",
    CA: "CA",
    NL: "NONE",
};
// The region list follows the ADDRESS country (US-06); the workspace only stands
// in while no country is selected.
export function addressRegionSet(addressCountry, workspaceCountry) {
    return addressCountry?.trim()
        ? REGION_SET_BY_FORMAT[detectCountryFormat(addressCountry)]
        : REGION_SET_BY_WORKSPACE[workspaceCountry];
}
// A region the new country has no list for would be saved without being shown.
export function setAddressCountry(values, country, workspaceCountry) {
    values.country = country;
    if (addressRegionSet(country, workspaceCountry) === "NONE")
        values.region = "";
}
// Replaces the street line. A layout with no house-number field shows no number beside it, so a
// number the record still holds describes the line this one REPLACES, not the new one - and
// toAddressLayout would fold it onto that new street at save time.
export function setAddressStreetLine(values, line1, layout) {
    values.line1 = line1;
    if (hasAddressField(layout, "houseNumber"))
        return;
    values.houseNumber = "";
    values.houseNumberSuffix = "";
}
// A Dutch suffix is a letter or sign plus at most four characters ("A", "-2", "hs",
// "bis"): a word longer than five characters after the number is not taken for one.
// A shorter city ("Delft", "Ede") still is; no length rules it out without breaking
// real suffixes.
const NUMBER_LAST = /^(.+?)\s+(\d+)\s*([^\d\s]\S{0,4})?$/;
const NUMBER_FIRST = /^(\d+)\s*(bis|ter|quater|[a-z])?,?\s+(.+)$/i;
function matchNumberLast(line) {
    const m = line.match(NUMBER_LAST);
    return m ? { street: m[1], houseNumber: m[2], houseNumberSuffix: m[3] ?? "" } : null;
}
function matchNumberFirst(line) {
    const m = line.match(NUMBER_FIRST);
    return m ? { street: m[3], houseNumber: m[1], houseNumberSuffix: m[2] ?? "" } : null;
}
export function splitStreetLine(line, order = "numberLast") {
    const trimmed = line.trim();
    const [preferred, other] = order === "numberFirst" ? [matchNumberFirst, matchNumberLast] : [matchNumberLast, matchNumberFirst];
    return (preferred(trimmed) ??
        other(trimmed) ?? { street: trimmed, houseNumber: "", houseNumberSuffix: "" });
}
// Moves the street of a row saved with StreetName (OP-4549 / OP-8051, migrated by OP-8957) into
// line1, then clears streetName: the preview reads line1 first, so a streetName left behind would
// never be shown again yet would still be saved. The house number is not its business -
// foldHouseNumberIn is the only place that writes it into line1, so the two cannot both do it.
export function carryStreetToLayout(values) {
    if (!values.line1.trim() && values.streetName.trim())
        values.line1 = values.streetName;
    values.streetName = "";
}
